import { GUI } from 'dat.gui'
import { Color } from 'three'

import { StoreScheme } from '../../../shared/store/store'
import { N8AOPostPass } from '../n8ao/main/N8AO'
import { N8AO } from '../../../../../trillion-common/src/objects/posteffects/n8ao.object'
import { N8AODto } from '../../../../../trillion-common/src/dto/posteffects/n8ao.dto'

const IRenderMode = {
  Combined: 0,
  AO: 1,
  No_AO: 2,
  Split: 3,
  Split_AO: 4,
}

const renderModes = ['Combined', 'AO', 'No AO', 'Split', 'Split AO'] as const
type RenderMode = (typeof renderModes)[number]

export function addN8aoGUI(
  gui: GUI,
  n8aoPass: N8AOPostPass,
  n8aoConfig: N8AODto | undefined,
  store: StoreScheme,
) {
  const setN8ao = store.getState().setN8ao

  const n8aoPassConfig = n8aoPass.configuration
  const n8aoOptions = {
    aoSamples: n8aoPassConfig.aoSamples ?? n8aoConfig?.aoSamples,
    denoiseSamples: n8aoPassConfig.denoiseSamples ?? n8aoConfig?.denoiseSamples,
    denoiseRadius: n8aoPassConfig.denoiseRadius ?? n8aoConfig?.denoiseRadius,
    aoRadius: n8aoPassConfig.aoRadius ?? n8aoConfig?.aoRadius,
    distanceFalloff: n8aoPassConfig.distanceFalloff ?? n8aoConfig?.distanceFalloff,
    gammaCorrection: n8aoPassConfig.gammaCorrection ?? n8aoConfig?.gammaCorrection,
    screenSpaceRadius: n8aoPassConfig.gammaCorrection ?? n8aoConfig?.screenSpaceRadius,
    halfRes: n8aoPassConfig.halfRes ?? n8aoConfig?.halfRes,
    depthAwareUpsampling: n8aoPassConfig.depthAwareUpsampling ?? n8aoConfig?.depthAwareUpsampling,
    enabled: n8aoConfig?.enabled ?? false,
    intensity: n8aoPassConfig.intensity ?? n8aoConfig?.intensity,
    renderMode: n8aoPassConfig.renderMode ?? 0,
    color: '#' + n8aoPassConfig.color.getHexString() ?? n8aoConfig?.color,
    colorMultiply: n8aoPassConfig.colorMultiply ?? n8aoConfig?.colorMultiply,
  }

  const n8aoFolder = gui.addFolder('N8AO')
  n8aoFolder.open()

  n8aoFolder
    .add(n8aoOptions, 'aoSamples', 1.0, 64.0, 1.0)
    .name('ao samples')
    .onChange((value: number) => {
      n8aoPass.configuration.aoSamples = value
      updateN8aoPass((n8ao) => {
        n8ao.aoSamples = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'denoiseSamples', 1.0, 64.0, 1.0)
    .name('denoise samples')
    .onChange((value: number) => {
      n8aoPass.configuration.denoiseSamples = value
      updateN8aoPass((n8ao) => {
        n8ao.denoiseSamples = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'denoiseRadius', 0.0, 24.0, 0.01)
    .name('denoise radius')
    .onChange((value: number) => {
      n8aoPass.configuration.denoiseRadius = value
      updateN8aoPass((n8ao) => {
        n8ao.denoiseRadius = value
      })
    })

  const aoRadius = n8aoFolder
    .add(n8aoOptions, 'aoRadius', 1.0, 10.0, 0.01)
    .name('ao radius')
    .onChange((value: number) => {
      n8aoPass.configuration.aoRadius = value
      updateN8aoPass((n8ao) => {
        n8ao.aoRadius = value
      })
    })
  const distanceFalloff = n8aoFolder
    .add(n8aoOptions, 'distanceFalloff', 0.0, 10.0, 0.01)
    .name('distance falloff')
    .onChange((value: number) => {
      n8aoPass.configuration.distanceFalloff = value
      updateN8aoPass((n8ao) => {
        n8ao.distanceFalloff = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'screenSpaceRadius')
    .name('screenspace radius')
    .onChange((value: number) => {
      if (value) {
        n8aoPass.configuration.aoRadius = 48.0
        n8aoPass.configuration.distanceFalloff = 0.2
        aoRadius.min(0)
        aoRadius.max(64)
        distanceFalloff.min(0)
        distanceFalloff.max(1)
      } else {
        n8aoPass.configuration.aoRadius = 5.0
        n8aoPass.configuration.distanceFalloff = 1.0
        aoRadius.min(1)
        aoRadius.max(10)
        distanceFalloff.min(0)
        distanceFalloff.max(10)
      }

      updateN8aoPass((n8ao) => {
        n8ao.aoRadius = value ? 48.0 : 5.0
        n8ao.distanceFalloff = value ? 0.2 : 1.0
      })

      aoRadius.updateDisplay()
      distanceFalloff.updateDisplay()
    })

  n8aoFolder
    .add(n8aoOptions, 'halfRes')
    .name('half resolution')
    .onChange((value: boolean) => {
      n8aoPass.configuration.halfRes = value
      updateN8aoPass((n8ao) => {
        n8ao.halfRes = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'gammaCorrection')
    .name('gamma correction')
    .onChange((value: boolean) => {
      n8aoPass.configuration.gammaCorrection = value
      updateN8aoPass((n8ao) => {
        n8ao.gammaCorrection = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'depthAwareUpsampling')
    .name('depth upsampling')
    .onChange((value: boolean) => {
      n8aoPass.configuration.depthAwareUpsampling = value
      updateN8aoPass((n8ao) => {
        n8ao.depthAwareUpsampling = value
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'intensity', 0.0, 20.0, 0.01)
    .name('ao intensity')
    .onChange((value: number) => {
      n8aoPass.configuration.intensity = value
      updateN8aoPass((n8ao) => {
        n8ao.intensity = value
      })
    })
  n8aoFolder
    .addColor(n8aoOptions, 'color')
    .name('color')
    .onChange((value: string) => {
      const color = new Color(value)
      n8aoPass.configuration.color = color
      updateN8aoPass((n8ao) => {
        n8ao.color = '#' + color.getHexString()
      })
    })
  n8aoFolder
    .add(n8aoOptions, 'colorMultiply')
    .name('color multiply')
    .onChange((value: boolean) => {
      n8aoPass.configuration.colorMultiply = value
      updateN8aoPass((n8ao) => {
        n8ao.colorMultiply = value
      })
    })

  const renderMode = n8aoFolder
    .add(n8aoOptions, 'renderMode', IRenderMode)
    .name('render mode')
    .onChange((value: RenderMode) => {
      const renderMode = Number(value)
      n8aoPass.configuration.renderMode = renderMode
    })
  n8aoFolder
    .add(n8aoOptions, 'enabled')
    .name('n8ao enabled')
    .onChange((value: boolean) => {
      n8aoPass.enabled = value
      toggleDisableRenderMode(renderMode.domElement, value)
      updateN8aoPass((n8ao) => {
        n8ao.enabled = value
      })
    })

  function toggleDisableRenderMode(domElement: HTMLElement, enabled: boolean) {
    domElement.style.pointerEvents = enabled ? 'auto' : 'none'
    domElement.style.opacity = enabled ? '1' : '0.5'
  }

  function updateN8aoPass(updateCallback: (n8ao: N8AO) => void) {
    updateCallback(n8aoOptions)
    setN8ao(n8aoOptions)
  }

  toggleDisableRenderMode(renderMode.domElement, n8aoOptions.enabled)

  setN8ao(n8aoOptions)
}
